/**
 * Слой звонков (изменение v1.14): входящий вызов, экран звонка, объяснение неудачи.
 *
 * Сессия WebRTC с криптографией подгружается на первом звонке (менеджер), всё остальное здесь.
 * Здесь же уборка «осиротевших» звонков этого устройства и тост «отвечено на другом устройстве».
 */
import { useEffect, useReducer, useRef } from 'react'

import { answeredElsewhere, deviceCallOf, incomingCallOf, useOpenCalls } from '@entities/call/model'
import { callManager } from '@features/calls/manager'
import { useCallStore } from '@features/calls/store'
import { callsRepo } from '@shared/db'
import { Button, Dialog, useToast } from '@shared/ui'

import { CallScreen } from './CallScreen'
import { IncomingCallScreen } from './IncomingCallScreen'

export function CallLayer({ deviceId }: { deviceId: string | null }) {
  const calls = useOpenCalls()
  const view = useCallStore((s) => s.view)
  const minimized = useCallStore((s) => s.minimized)
  const accepting = useCallStore((s) => s.acceptingId)
  const notice = useCallStore((s) => s.notice)
  const setNotice = useCallStore((s) => s.setNotice)
  const toast = useToast()
  const [, tick] = useReducer((n: number) => n + 1, 0)
  const lastIncoming = useRef<string | null>(null)

  const incoming = incomingCallOf(calls)

  // Вызов без ответа гаснет по сроку, даже если событие о его завершении ещё в пути.
  useEffect(() => {
    if (!incoming) return
    const left = Date.parse(incoming.ringing_until) - Date.now()
    const timer = setTimeout(tick, Math.max(0, left) + 50)
    return () => clearTimeout(timer)
  }, [incoming])

  // Звонок этого устройства, которого не ведёт ни одна вкладка, завершается сразу.
  const orphan = deviceCallOf(calls, deviceId)
  useEffect(() => {
    if (orphan && !callManager.busy()) void callManager.endOrphan(orphan)
  }, [orphan])

  // «Отвечено на другом устройстве» — вместо молча исчезнувшего экрана вызова.
  useEffect(() => {
    if (incoming) {
      lastIncoming.current = incoming.id
      return
    }
    const id = lastIncoming.current
    lastIncoming.current = null
    if (!id || accepting === id) return
    void callsRepo.get(id).then((call) => {
      if (call && answeredElsewhere(call, deviceId)) toast.show('Отвечено на другом устройстве')
    })
  }, [incoming, accepting, deviceId, toast])

  // Сессия переживает переходы, но не выход из аккаунта.
  useEffect(() => {
    void callsRepo.pruneEnded().catch(() => undefined)
    return () => callManager.shutdown()
  }, [])

  return (
    <>
      {view && !minimized ? <CallScreen view={view} /> : null}
      {!view && incoming && accepting !== incoming.id ? <IncomingCallScreen call={incoming} /> : null}
      <Dialog
        open={notice !== null}
        onClose={() => setNotice(null)}
        title={notice?.title ?? ''}
        description={notice?.text}
        footer={<Button onClick={() => setNotice(null)}>Понятно</Button>}
      />
    </>
  )
}
